// ◉ xlVISION · Powering Business Together!
// © ExcelVision, Lda. — Braga, Portugal
// ( ·:·●●·:· ) xlVISION | Powering Business Together!
//
// ============================================================================
// A CIDADE DE UM DIA — uma só decisão, para a FROTA, os PARCEIROS e a ESPERA
// ============================================================================
//
// 📗 Cliente, 2026-09-16: *«Subcontratados com UI muito parecido no mapa aos pedidos
//    convencionais, com as cidades por dias seguindo a mesma estrutura. Para além disso,
//    quando movo um pedido para o final ficando pendente quero que também guarde a UI pelas
//    cidades por dia e não aparecer apenas a linha única entre várias outras.»*
//
// 📗 E já o tinha pedido uma vez, em §4.3 do desenho da agilidade: *«Os subcontratados têm de
//    dar para ver os dias igualmente como se fosse um autocarro normal; a única diferença é
//    que não se indica o motorista.»* — e o `Mapa.tsx` trazia a recusa **escrita como
//    limitação**: *«a linha do subcontratado tem UMA banda de 30 px… não há onde desenhar a
//    localidade de cada dia»*.
//
// ---------------------------------------------------------------------------
// ⚠️⚠️ PORQUE É QUE ISTO É UM MÓDULO E NÃO TRÊS BLOCOS PARECIDOS
// ---------------------------------------------------------------------------
//
//   A decisão «que cidade é que este dia mostra» tem **quatro** saídas e nenhuma é óbvia:
//
//     · `nada`     — o dia não tem célula, ou é um dia de OFICINA/AVARIA (`c[7] >= 0`) ou de
//                    ROTAÇÃO, onde a banda 2 da frota é ocupada por outra coisa (as horas);
//     · `falta`    — há célula e **não** há localidade → **`?` em caixa tracejada** (D-081).
//                    Medido na folha real: **5,9 %** dos dias dentro de uma corrida;
//     · `repetido` — a viatura está PARADA no mesmo sítio de ontem → não se repete o código;
//     · `local`    — mostra-se, com o embarque (F-21), o sentido e o meio.
//
//   📌 Três blocos parecidos é a regra 8 à letra: *«duas formulações divergem, e a que divergir
//   é a que ninguém corre»*. E aqui a divergência teria consequência de negócio — a cidade de
//   um dia **é** a pernoita, que decide o alojamento pago (D-060) e a prova do repouso semanal
//   (art. 8.º n.º 8). Uma secção que decidisse `falta` onde a outra decide `repetido` mostrava
//   um branco onde a outra mostra um `?`, e **o branco lê-se como «está tudo bem»**.
//
// ---------------------------------------------------------------------------
// ⚠️⚠️ O QUE É PARTILHADO É A **DECISÃO**, E NÃO A MARCAÇÃO — e o porquê tem número
// ---------------------------------------------------------------------------
//
//   A frota desenha a cidade **dentro da célula `.dia`**, que existe na frota porque é o alvo
//   do rato. A secção dos parceiros e a da espera **não têm células de dia**: têm barras
//   posicionadas em absoluto sobre a faixa. Medido
//   (`tools/comparar-mapa/sonda-cidades-por-dia-no-subcontratado.mjs`, folha real, pior janela
//   de 40 dias, 35 linhas não-próprias):
//
//   | desenho | nós acrescentados | o mapa inteiro (3 246) |
//   |---|---|---|
//   | copiar a célula `.dia` da frota à letra | **+1 400** de células VAZIAS, mais as cidades | +63 % |
//   | a marcação da frota: `span` + `-txt` (**+1 286**) e as setas (**+304**) | **+1 590**, e ainda o ícone de meio | +49 % |
//   | **uma cidade por dia num só nó** ← escolhido | **+643** | **+19,8 %** |
//
//   ⚠️ O mapa inteiro mede **3 246 nós** com p95 de 29,5–45,2 ms (ADR-001, risco R1), e este
//   projecto já recusou um ícone em 19 % das barras porque *«~2 nós cada em 600 dias visíveis,
//   e o p95 nota-o»*. **Entre as duas marcações vão +947 nós, 29 % do mapa inteiro** — logo a
//   marcação **não** se partilha, e fica declarado o que se perde: nesta secção o embarque não
//   tem tom próprio (vai no mesmo nó, `MAD/LIS`), não há seta de chegada/partida e não há
//   ícone de meio.
//
//   ⚠️ **«Só quando muda» foi medido e NÃO se aplica:** pouparia **142 nós (22,1 %)**, e a
//   frota só suprime repetições num dia **parado** — os dias de um parceiro são viagem, e
//   📗 *«tens de colocar a pernoita do dia»* vale para eles como vale para a frota.
//
//   📌 **O que se partilha é tudo o que decide, e as CLASSES** — `dia-local`,
//   `dia-local--falta` e `dia-local--inferido` são as mesmas nas três secções, porque são a
//   **mesma coisa**: a cidade de um dia. Dar-lhes nome novo é que seria F-20 ao contrário.

import type { CelulaCompacta, DadosMapa } from '@xlv-gva-mpt/dominio';
import {
  BANDEIRA,
  comMarcaDePresumida,
  destinoFinalDoPedido,
  locaisRegistadosPorDia,
  localDoDiaDoServico,
} from '@xlv-gva-mpt/dominio';
import { rotuloDia } from './celula.js';
import { natureza } from './segmentos.js';
import { dataDoIndice, janelaDoAno } from '@xlv-gva-mpt/dominio';
import type { JanelaDoMapa } from '@xlv-gva-mpt/dominio';

/**
 * ⚠️ **Os dois `title` vivem aqui porque são a mesma frase nas três secções.** Um texto que
 * explica um sinal e que diverge entre secções ensina que os sinais são diferentes.
 */
export const TITULO_LOCAL_EM_FALTA =
  'Local por definir. Nos dias sem chegada nem partida o local é onde o motorista dorme — e é o que determina se o repouso é legal (art. 8.º n.º 8) e se há alojamento a pagar.';

export const TITULO_LOCAL_INFERIDO =
  'local INFERIDO — é onde a viatura estava, não onde alguém decidiu que dorme';

/** O que um dia mostra na banda da localidade. */
export type LocalDoDia =
  /** Não há banda de localidade neste dia — sem célula, oficina/avaria, ou dia de rotação. */
  | { readonly tipo: 'nada' }
  /** Há célula e não há localidade: **`?` em caixa tracejada** (D-081). */
  | { readonly tipo: 'falta' }
  /** Parada no mesmo sítio de ontem: não se repete o código. */
  | { readonly tipo: 'repetido'; readonly local: string }
  | {
      readonly tipo: 'local';
      readonly local: string;
      /** Onde os PASSAGEIROS entram, no primeiro dia (F-21, 4,6 % das células). */
      readonly embarque: string;
      readonly sentido: 'chegada' | 'partida' | null;
      /** A reserva preenche todos os dias e só conhece o primeiro e o último. */
      readonly inferido: boolean;
      /**
       * Migração 093 · o local do último dia foi PRESUMIDO (o pedido não tem largada). O `?`
       * já vem colado em `local`; isto existe para o `title` dizer porquê.
       */
      readonly presumido: boolean;
      /** `''` quando não há meio declarado. Índice `c[8]` resolvido em `dados.meios`. */
      readonly meio: string;
    };

/**
 * ⚠️ **A banda 2 só existe num dia «normal».** Um dia de oficina/avaria (`c[7] >= 0`) e um dia
 * de rotação têm outra coisa ali — e chamar `rotuloDia` num deles devolvia uma localidade que
 * o ecrã não mostra, logo a comparação com o dia anterior passava a ser feita contra um valor
 * que ninguém vê.
 */
function ehNormal(c: CelulaCompacta | null): c is CelulaCompacta {
  return c !== null && c[7] < 0 && (c[4] & BANDEIRA.ROTACAO) === 0;
}

/**
 * Que cidade é que o dia mostra, dado o dia e o **dia anterior**.
 *
 * ⚠️⚠️ **`anterior` é `null` no PRIMEIRO DIA DA JANELA, e isso não é um pormenor.** A frota
 * repete sempre o código na primeira coluna desenhada — *«senão a coluna da esquerda ficava
 * sem contexto ao deslocar»*. Passar aqui o dia que está fora da janela fazia a cidade
 * desaparecer ao deslocar o mapa um dia, e reaparecer ao deslocar outro: o mapa a mudar de
 * conteúdo por causa da posição do scroll.
 *
 * ⚠️ **Só se suprime num dia PARADO — nunca numa viagem.** 📗 Cliente, 2026-08-26: *«tens de
 * colocar a pernoita do dia, para quem olhar para o mapa e perceber onde o autocarro vai
 * pernoitar»*. Uma viagem de vários dias pela mesma cidade mostra-a em **todos** os dias.
 * 📌 É também por isto que a poupança de *«só quando muda»* não se aplica aos parceiros: os
 * dias deles são viagem, e medido na folha real só **22,1 %** repetiriam.
 */
export function localDoDia(
  dados: DadosMapa,
  celula: CelulaCompacta | null,
  anterior: CelulaCompacta | null,
): LocalDoDia {
  if (!ehNormal(celula)) return { tipo: 'nada' };
  const r = rotuloDia(dados, celula);
  if (r.local === '') return { tipo: 'falta' };

  const rAnterior = ehNormal(anterior) ? rotuloDia(dados, anterior) : null;
  const estaParado = natureza(celula)?.tipo === 'parado';
  if (
    estaParado &&
    rAnterior !== null &&
    r.local === rAnterior.local &&
    r.sentido === null &&
    r.embarque === ''
  ) {
    return { tipo: 'repetido', local: r.local };
  }

  return {
    tipo: 'local',
    local: r.local,
    embarque: r.embarque,
    sentido: r.sentido,
    inferido: (celula[4] & BANDEIRA.LOCAL_INFERIDO) !== 0,
    presumido: r.presumido,
    meio: celula[8] >= 0 ? (dados.meios[celula[8]] ?? '') : '',
  };
}

/**
 * O texto de **um só nó** — a forma que as secções sem células de dia usam.
 *
 * ⚠️ O embarque entra no MESMO nó (`MAD/LIS`) em vez de num `<span>` mais fraco. É a única
 * diferença de leitura face à frota, e é deliberada: um nó por dia contra dois são **+643
 * nós** na pior janela, 20 % do mapa inteiro. 📌 A informação **não se perde** — muda de tom,
 * não de sítio.
 */
export function textoDoLocal(d: LocalDoDia): string {
  if (d.tipo === 'falta') return '?';
  if (d.tipo !== 'local') return '';
  return d.embarque === '' ? d.local : `${d.embarque}/${d.local}`;
}

// ============================================================================
// 🔴 2026-10-08 · A CIDADE DE CADA DIA DE UM PEDIDO QUE AINDA NÃO TEM AUTOCARRO
// ============================================================================
//
// 📗 Cliente, 2026-10-08: nas secções do fundo do mapa *«tem de aparecer para além da linha
//    simples as cidades também e todos os outros detalhes como acontece nas alocações normais»*.
//
// 📌 **É a cidade que a RESERVA vai escrever, e não uma parecida:** as duas saem de
//    `locaisRegistadosPorDia` + `localDoDiaDoServico` (domínio), que é o código que a reserva
//    corre. O que aqui se acrescenta é só o que a célula da frota diria desse dia — o mesmo
//    `LocalDoDia`, para a banda o desenhar com as mesmas classes e os mesmos `title`.

/** O mínimo de um ponto do percurso — `PontoPedido` satisfaz-o. */
export interface PontoDoPedidoNoMapa {
  readonly tipo: 'recolha' | 'pernoita' | 'largada';
  readonly data: string;
  readonly hora: string;
  readonly localidade: string;
}

/** Um dia de um pedido na banda das cidades: o que se desenha e a hora que o explica. */
export interface DiaDoPedido {
  readonly d: number;
  readonly local: LocalDoDia;
  /** `''` quando o dia não tem hora marcada (ou não é de recolha nem de largada). */
  readonly hora: string;
}

/**
 * Os dias `dia0..dia1` de um pedido, com a cidade de cada um.
 *
 * - **Registado** (há ponto nesse dia) ganha; sem ele, o primeiro dia é a recolha, o último o
 *   destino final e o meio herda a recolha — e diz-se **inferido**, como na frota.
 * - **Presumido** (migração 093): o último dia sem largada leva o `?` colado e o mesmo `title`.
 * - **Embarque** (F-21): no dia da recolha, se a pernoita é noutra cidade, as duas — `LIS/MAD`.
 * - **Sentido**: a recolha é o `in` (chegada do grupo), a largada o `out` — F-25.
 * - **Falta** quando não se sabe nada: o `?` tracejado de D-081, e nunca uma cidade inventada.
 */
export function diasDoPedido(o: {
  readonly janela: JanelaDoMapa;
  readonly dia0: number;
  readonly dia1: number;
  readonly pontos: readonly PontoDoPedidoNoMapa[];
}): readonly DiaDoPedido[] {
  const dataDe = (d: number): string =>
    dataDoIndice(o.janela, d);
  const registados = locaisRegistadosPorDia(
    o.pontos.map((p) => ({ dia: p.data, localidade: p.localidade, tipo: p.tipo })),
  );
  const recolha = o.pontos.find((p) => p.tipo === 'recolha' && p.localidade !== '') ?? null;
  const destino = destinoFinalDoPedido(o.pontos);
  const saida: DiaDoPedido[] = [];
  for (let d = o.dia0; d <= o.dia1; d++) {
    const data = dataDe(d);
    const r = localDoDiaDoServico({
      localPorDia: registados,
      data,
      d,
      diaEmbarque: o.dia0,
      dia1: o.dia1,
      localidadeId: recolha?.localidade ?? null,
      localidadeFimId: destino?.localidade ?? null,
    });
    const doDia = o.pontos.filter((p) => p.data === data);
    const entra = doDia.find((p) => p.tipo === 'recolha');
    const sai = doDia.find((p) => p.tipo === 'largada');
    const hora = [entra?.hora ?? '', sai?.hora ?? ''].filter((h) => h !== '').join(' · ');
    if (r.local === null || r.local === '') {
      saida.push({ d, local: { tipo: 'falta' }, hora });
      continue;
    }
    const presumido =
      d === o.dia1 && !r.registado && destino !== null && destino.qualidade === 'presumida';
    const embarque =
      entra !== undefined && entra.localidade !== '' && entra.localidade !== r.local
        ? entra.localidade
        : '';
    saida.push({
      d,
      hora,
      local: {
        tipo: 'local',
        local: presumido ? comMarcaDePresumida(r.local) : r.local,
        embarque,
        sentido: entra !== undefined ? 'chegada' : sai !== undefined ? 'partida' : null,
        inferido: !r.registado,
        presumido,
        meio: '',
      },
    });
  }
  return saida;
}
